import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { Book } from '@/types';
import { 
  BookOpenIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  FireIcon,
  SparklesIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import Icon from '@/components/Icon';
import BookCover from '@/components/BookCover';

interface MonthWrapupData {
  month: string;
  year: number;
  books_finished: Book[];
  total_pages_read: number;
  reading_sessions: number;
  average_pages_per_day: number;
  reading_streak: number;
  top_genres: Array<{ genre: string; count: number }>;
  monthly_stats: {
    total_books: number;
    finished_books: number;
    currently_reading: number;
    want_to_read: number;
  };
}

const MonthWrapupPage: React.FC = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
  const currentYear = now.getFullYear();

  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const resetImage = () => {
    setImageReady(false);
    setImageFailed(false);
    setImageError(null);
    setImageVersion((v) => v + 1);
  };

  const [wrapupData, setWrapupData] = useState<MonthWrapupData | null>(null);
  // Shareable image state. The <img> is the probe: if the endpoint cannot render
  // (no finished books, unfetchable cover, missing font) the browser fires onError
  // and we show the reason instead of a broken image.
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageVersion, setImageVersion] = useState(0);
  // Which month the shareable image is for, independent of the page's month.
  // (The legacy UI generated the previous month's collage, which is why the old
  // view so often had nothing to show.)
  const [imageMonth, setImageMonth] = useState(currentMonth === 1 ? 12 : currentMonth - 1);
  const [imageYear, setImageYear] = useState(
    currentMonth === 1 ? currentYear - 1 : currentYear
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  // Hoisted out of the fetch effect so the shareable-image URLs below can use them.

  useEffect(() => {
    const fetchMonthWrapup = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Try to fetch month wrapup data
        const response = await api.get<any>(`/reports/month-wrapup/${currentYear}/${currentMonth}`);
        
        if (response.success && response.data) {
          const data = response.data;
          // Map backend shape to UI shape
          const mapped: MonthWrapupData = {
            month: data.month_name || new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'long' }),
            year: data.year,
            books_finished: data.finished_books || [],
            total_pages_read: data.statistics?.total_pages_read || 0,
            reading_sessions: data.statistics?.total_reading_days || 0,
            average_pages_per_day: data.statistics?.average_pages_per_day || 0,
            reading_streak: data.statistics?.reading_streak || 0,
            top_genres: data.top_genres || [],
            monthly_stats: {
              total_books: (data.finished_books || []).length + (data.currently_reading || 0),
              finished_books: (data.finished_books || []).length,
              currently_reading: data.currently_reading || 0,
              want_to_read: data.want_to_read || 0,
            },
          };
          setWrapupData(mapped);
          setIsEmpty(false);
        } else {
          // If no data, show empty state
          setIsEmpty(true);
          setWrapupData({
            month: new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'long' }),
            year: currentYear,
            books_finished: [],
            total_pages_read: 0,
            reading_sessions: 0,
            average_pages_per_day: 0,
            reading_streak: 0,
            top_genres: [],
            monthly_stats: {
              total_books: 0,
              finished_books: 0,
              currently_reading: 0,
              want_to_read: 0
            }
          });
        }
      } catch (err) {
        setError('Failed to load month wrap-up data');
        console.error('Month wrapup fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMonthWrapup();
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-base-content">Month Wrap-up</h1>
          <p className="text-base-content/70 mt-1">Loading your monthly reading statistics...</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat bg-base-100 rounded-box animate-pulse">
              <div className="stat-title bg-base-300 h-4 rounded mb-2"></div>
              <div className="stat-value bg-base-300 h-8 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-base-content">Month Wrap-up</h1>
          <p className="text-base-content/70 mt-1">Your monthly reading statistics</p>
        </div>
        
        <div className="bg-base-100 border-2 border-error/20 rounded-2xl p-8 shadow-lg text-center">
          <h2 className="text-2xl font-bold text-error mb-4">Failed to Load Month Wrap-up</h2>
          <p className="text-base-content/70 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn btn-outline btn-error"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-base-content">Month Wrap-up</h1>
        <p className="text-base-content/70 mt-1">
          {wrapupData?.month} {wrapupData?.year} Reading Summary
        </p>
      </div>

      {isEmpty ? (
        // Empty State - No books finished this month
        <div className="hero bg-base-300 py-12 rounded-box mb-8">
          <div className="hero-content text-center">
            <div>
              <h1 className="text-4xl font-bold mb-4">Month Wrap Up - {wrapupData?.month} {wrapupData?.year}
              </h1>
              <p className="text-xl opacity-90">See your monthly reading summary</p>
            </div>
          </div>
        </div>
      ) : (
        // Month Wrap-up Content
        <>
          {/* Monthly Statistics */}
          <div className="stats shadow bg-base-100">
            <div className="stat">
              <div className="stat-figure text-base-content/50">
                <Icon hero={<BookOpenIcon className="w-8 h-8" />} emoji="📚" />
              </div>
              <div className="stat-title">Books Finished</div>
              <div className="stat-value tnum">{wrapupData?.books_finished?.length || 0}</div>
              <div className="stat-desc">This month</div>
            </div>
            
            <div className="stat">
              <div className="stat-figure text-base-content/50">
                <Icon hero={<ArrowTrendingUpIcon className="w-8 h-8" />} emoji="📈" />
              </div>
              <div className="stat-title">Pages Read</div>
              <div className="stat-value tnum">{wrapupData?.total_pages_read || 0}</div>
              <div className="stat-desc">Total pages</div>
            </div>
            
            <div className="stat">
              <div className="stat-figure text-base-content/50">
                <Icon hero={<ClockIcon className="w-8 h-8" />} emoji="⏱️" />
              </div>
              <div className="stat-title">Reading Sessions</div>
              <div className="stat-value tnum">{wrapupData?.reading_sessions || 0}</div>
              <div className="stat-desc">This month</div>
            </div>
            
            <div className="stat">
              <div className="stat-figure text-base-content/50">
                <Icon hero={<FireIcon className="w-8 h-8" />} emoji="🔥" />
              </div>
              <div className="stat-title">Reading Streak</div>
              <div className="stat-value tnum">{wrapupData?.reading_streak || 0}</div>
              <div className="stat-desc">Consecutive days</div>
            </div>
          </div>

          {/* Average Pages Per Day */}
          {wrapupData && wrapupData.average_pages_per_day > 0 && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title text-primary mb-4">
                  <Icon hero={<ChartBarIcon className="w-6 h-6" />} emoji="📊" />
                  Daily Reading Average
                </h2>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {wrapupData.average_pages_per_day.toFixed(1)}
                  </div>
                  <p className="text-base-content/70">pages per day</p>
                </div>
              </div>
            </div>
          )}

          {/* Shareable image. The Flask templates served this all along and the
              README leads with it, but the SPA never called it, so the feature
              was unreachable for real users. */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title text-primary mb-4">
                <Icon hero={<SparklesIcon className="w-6 h-6" />} emoji="🖼️" />
                Shareable Wrap-up Image
              </h2>
              <p className="text-base-content/70 mb-4">
                A collage of every book you finished in a month, ready to share.
              </p>

              <div className="flex flex-wrap items-end gap-3 mb-4">
                <label className="form-control">
                  <span className="label-text mb-1">Month</span>
                  <select
                    className="select select-bordered select-sm"
                    value={imageMonth}
                    onChange={(e) => {
                      setImageMonth(Number(e.target.value));
                      resetImage();
                    }}
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-control">
                  <span className="label-text mb-1">Year</span>
                  <select
                    className="select select-bordered select-sm"
                    value={imageYear}
                    onChange={(e) => {
                      setImageYear(Number(e.target.value));
                      resetImage();
                    }}
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {imageError && (
                <div className="alert alert-warning mb-4">
                  <span>{imageError}</span>
                </div>
              )}

              {/*
                The <img> is always mounted: its own onLoad/onError are what tell
                us whether the server could render. An earlier version gated it on
                `!loading`, but only onLoad clears that flag, so the element never
                mounted and the spinner ran forever. It is hidden rather than
                unmounted so the request still happens.
              */}
              <div className={imageFailed ? 'hidden' : 'block'}>
                <div className="flex items-center gap-3 mb-3">
                  {!imageReady && (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      <span className="text-base-content/70 text-sm">Building your image...</span>
                    </>
                  )}
                </div>
                <img
                  key={`${imageYear}-${imageMonth}-${imageVersion}`}
                  src={api.reports.monthWrapupImageUrl(imageYear, imageMonth, false)}
                  alt={`Books finished in ${imageYear}-${String(imageMonth).padStart(2, '0')}`}
                  className={`w-full max-w-md rounded-lg border border-base-300 shadow ${
                    imageReady ? '' : 'hidden'
                  }`}
                  onLoad={() => setImageReady(true)}
                  onError={() => {
                    setImageFailed(true);
                    setImageError(
                      'No image for that month yet, or it could not be rendered. That usually ' +
                        'means no books were finished in the month you picked, or a book cover ' +
                        'could not be fetched.'
                    );
                  }}
                />
              </div>

              {(imageReady || imageError || imageFailed) && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {imageReady && (
                    <a
                      href={api.reports.monthWrapupImageUrl(imageYear, imageMonth, true)}
                      className="btn btn-primary"
                      download
                    >
                      <Icon hero={<ArrowDownTrayIcon className="w-5 h-5" />} emoji="⬇️" />
                      Download
                    </a>
                  )}
                  <button onClick={resetImage} className="btn btn-outline">
                    Regenerate
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {isEmpty && (
        <div className="flex justify-center mb-8">
          <div className="card bg-base-100 shadow-xl w-full max-w-2xl">
            <div className="card-body items-center text-center py-12">
              <div className="mb-6">
                <span className="text-6xl"></span>
              </div>
              <h2 className="text-2xl font-bold mb-3">No Books Finished This Month</h2>
              <p className="text-base-content/70 mb-6">
                You haven't finished any books in {wrapupData?.month} {wrapupData?.year} yet. <br />
                Keep reading to see your amazing monthly wrap-up!
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link to="/add-book" className="btn btn-primary">
                  ➕ Add a Book
                </Link>
                <Link to="/library" className="btn btn-outline">
                  📚 View Library
                </Link>
                <Link to="/" className="btn btn-outline btn-primary">
                  🏠 Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finished Books List */}
      {wrapupData && wrapupData.books_finished && wrapupData.books_finished.length > 0 && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title text-primary mb-6">
              <Icon hero={<CheckCircleIcon className="w-6 h-6" />} emoji="✅" />
              Books Finished This Month
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {wrapupData.books_finished.map((book) => (
                <div key={book.uid} className="flex items-center gap-3 p-3 bg-base-200 rounded-lg">
                  <div className="w-12 h-16 bg-base-300 rounded-lg overflow-hidden flex-shrink-0">
                    <BookCover
                      src={book.cover_url?.trim() || '/static/bookshelf.png'}
                      alt={`${book.title} cover`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold truncate">{book.title}</h3>
                    <p className="text-sm text-base-content/70 truncate">{book.author}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="badge badge-success badge-xs">Finished</span>
                      {book.finish_date && (
                        <span className="text-xs text-base-content/60">
                          {new Date(book.finish_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Genres */}
      {wrapupData && wrapupData.top_genres?.length > 0 && (
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title text-primary mb-6">
              <Icon hero={<ChartBarIcon className="w-6 h-6" />} emoji="📊" />
              Top Genres This Month
            </h2>
            
            <div className="space-y-3">
              {wrapupData.top_genres.map((genre, index) => (
                <div key={genre.genre} className="flex items-center justify-between p-3 bg-base-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-primary">#{index + 1}</span>
                    <span className="font-semibold">{genre.genre}</span>
                  </div>
                  <span className="badge badge-primary">{genre.count} books</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthWrapupPage;
